import React, {useState, useEffect} from 'react'
import {Modal, Button, Table, Form, InputGroup, Spinner, Alert} from 'react-bootstrap'
import {useLazyQuery, useMutation} from '@apollo/client'
import {GET_ALL_CONTACTS_API} from '../../../gql/queries/contactsApiQueries'
import {ADD_TO_RECIPIENTS} from '../../../gql/mutations/recipients'
import {IResContactsAPI} from '../../../types/contactsApi'
import {PhoneProviderActions} from '../../../components/telephony/PhoneProviderActions'
import {toast} from 'react-toastify'
import './AddRecipientsModal.css'

interface AddRecipientsModalProps {
  show: boolean
  onHide: () => void
  onSuccess?: () => void
}

const PAGE_SIZE = 20

export const AddRecipientsModal: React.FC<AddRecipientsModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [currentPage, setCurrentPage] = useState(1)

  const [getContacts, {data, loading}] = useLazyQuery<IResContactsAPI>(GET_ALL_CONTACTS_API, {
    fetchPolicy: 'network-only',
  })

  const [addToRecipients, {loading: adding}] = useMutation(ADD_TO_RECIPIENTS)

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch NON-recipient contacts
  useEffect(() => {
    if (show) {
      const offset = (currentPage - 1) * PAGE_SIZE
      getContacts({
        variables: {
          name: debouncedSearch || undefined,
          first: PAGE_SIZE,
          offset,
          isRecipient: false, // Only NON-recipients
        },
      })
    }
  }, [show, debouncedSearch, currentPage, getContacts])

  const contacts = data?.allContacts?.edges || []
  const totalCount = data?.allContacts?.totalCount || 0
  const totalPages = Math.ceil(totalCount / PAGE_SIZE)

  const toggleSelect = (contactId: string) => {
    setSelectedIds(prev =>
      prev.includes(contactId)
        ? prev.filter(id => id !== contactId)
        : [...prev, contactId]
    )
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length && contacts.length > 0) {
      setSelectedIds([])
    } else {
      setSelectedIds(contacts.map(edge => edge.node.id))
    }
  }

  const handleAdd = async () => {
    if (selectedIds.length === 0) {
      toast.error('Please select at least one contact')
      return
    }

    try {
      const result = await addToRecipients({
        variables: {contactIds: selectedIds},
      })
      toast.success(`${result.data.addToRecipients.addedCount} contact(s) added to recipients`)
      handleClose()
      onSuccess?.()
    } catch (error: any) {
      toast.error(error.message || 'Failed to add contacts to recipients')
    }
  }

  const handleClose = () => {
    setSelectedIds([])
    setSearchTerm('')
    setCurrentPage(1)
    onHide()
  }

  const isAllSelected = contacts.length > 0 && selectedIds.length === contacts.length

  return (
    <Modal show={show} onHide={handleClose} size='xl' centered>
      <Modal.Header closeButton>
        <Modal.Title>Add Recipients</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='mb-4'>
          <Alert variant='info'>
            <i className='bi bi-info-circle me-2'></i>
            Select contacts to add to your recipients list. Only contacts not already in the list are shown.
          </Alert>

          <InputGroup>
            <InputGroup.Text>
              <i className='bi bi-search'></i>
            </InputGroup.Text>
            <Form.Control
              type='text'
              placeholder='Search by name, email, or phone...'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </div>

        {selectedIds.length > 0 && (
          <Alert variant='primary' className='mb-3'>
            <strong>{selectedIds.length}</strong> contact(s) selected
          </Alert>
        )}

        <div className='table-container' style={{maxHeight: '400px', overflowY: 'auto'}}>
          {loading ? (
            <div className='text-center py-5'>
              <Spinner animation='border' />
            </div>
          ) : contacts.length === 0 ? (
            <Alert variant='warning'>
              No contacts available to add. All contacts are already in your recipients list.
            </Alert>
          ) : (
            <Table hover size='sm'>
              <thead style={{position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1}}>
                <tr>
                  <th style={{width: '50px'}}>
                    <Form.Check
                      type='checkbox'
                      checked={isAllSelected}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                </tr>
              </thead>
              <tbody>
                {contacts.map(({node: contact}) => (
                  <tr key={contact.id}>
                    <td>
                      <Form.Check
                        type='checkbox'
                        checked={selectedIds.includes(contact.id)}
                        onChange={() => toggleSelect(contact.id)}
                      />
                    </td>
                    <td>
                      <strong>{contact.fullName || `${contact.firstName} ${contact.lastName}`}</strong>
                    </td>
                    <td>{contact.email || <span className='text-muted'>—</span>}</td>
                    <td>
                      <PhoneProviderActions phone={contact.phone} emptyText='—' />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </div>

        {totalPages > 1 && (
          <div className='d-flex justify-content-between align-items-center mt-3'>
            <div className='text-muted small'>
              Showing {contacts.length} of {totalCount} contacts
            </div>
            <div className='d-flex gap-2'>
              <Button
                size='sm'
                variant='outline-secondary'
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => prev - 1)}
              >
                Previous
              </Button>
              <span className='align-self-center small'>
                Page {currentPage} of {totalPages}
              </span>
              <Button
                size='sm'
                variant='outline-secondary'
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(prev => prev + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={handleClose}>
          Cancel
        </Button>
        <Button
          variant='primary'
          onClick={handleAdd}
          disabled={adding || selectedIds.length === 0}
        >
          {adding ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' />
              Adding...
            </>
          ) : (
            <>
              Add {selectedIds.length > 0 && `(${selectedIds.length})`}
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
